import Swal from "sweetalert2";
import React, { useState, useEffect, useRef } from "react";

import { Toast } from "react-bootstrap";
import AddVirtualCommute from "./Modals/AddVirtualCommute";
import EditVirtualCommute from "./Modals/EditVirtualCommute";

import axios from "axios";
import {
  CREATE_VIRTUAL_COMMUTE,
  DELETE_VIRTUAL_COMMUTE,
  GET_VIRTUAL_COMMUTE,
  UPDATE_VIRTUAL_COMMUTE,
  UPDATE_VIRTUALCOMMUTE_AVAILABILITY,
  UPDATE_VIRTUALCOMMUTE_POSITION,
} from "URLRoutes";
import { generateURLOptions } from "URLOptions";

import { TiPlus } from "react-icons/ti";
import FilledButton from "components/InterstellrComponents/FilledButton";
import VirtualCommuteItem from "./VirtualCommuteItem";

// 👇 Snapdrag
import { useDraggable, useDroppable, Overlay } from "snapdrag";

const GRID_COLUMNS = 5;
const GAP = 8;

// --- Draggable Wrapper ---
const VirtualCommuteItemDraggable = ({
  item,
  index,
  kind,
  onEdit,
  onDelete,
  onPlay,
  onPause,
  onChangeStatus,
}) => {
  const { draggable, isDragging } = useDraggable({
    kind,
    data: { id: item._id, item, index },
    move: true,
  });

  return draggable(
    <div
      className="border p-2 bg-white rounded-md"
      style={{ opacity: isDragging ? 0.2 : 1 }}
    >
      <VirtualCommuteItem
        item={item}
        onEdit={onEdit}
        onDelete={onDelete}
        onPlay={onPlay}
        onPause={onPause}
        onChangeStatus={onChangeStatus}
      />
    </div>
  );
};

const VirtualCommute = () => {
  const [virtualCommutes, setVirtualCommutes] = useState([]);

  const [showAddVirtualCommute, setShowAddVirtualCommmute] = useState(false);
  const [selectedCommute, setSelectedCommute] = useState(null);
  const [showEditCommute, setShowEditCommute] = useState(false);

  const [showAudio, setShowAudio] = useState(false);
  const [playAudio, setPlayAudio] = useState("");

  const containerRef = useRef(null);
  const kind = "VIRTUAL_COMMUTE";

  const handleAudioPlay = (event) => {
    setPlayAudio(event);
    setShowAudio(true);
  };

  const handleAudioStop = () => {
    setPlayAudio("");
    setShowAudio(false);
  };

  const editCommute = (data) => {
    setSelectedCommute(data);
    setShowEditCommute(true);
  };

  useEffect(() => {
    document.title = "Virtual Commute | Administration Panel | Mind Pulse ";
    getVirtualCommute();
  }, []);

  const getVirtualCommute = async () => {
    try {
      const response = await axios.get(GET_VIRTUAL_COMMUTE, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setVirtualCommutes(response?.data?.data);
    } catch (error) {
      console.error("Get Virtual Commute Items Error: ", error);
      alert(error);
    }
  };

  const addVirtualCommute = async (data) => {
    try {
      const response = await axios.post(CREATE_VIRTUAL_COMMUTE, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setShowAddVirtualCommmute(false);
      setTimeout(() => {
        getVirtualCommute();
      }, 250);
    } catch (error) {
      console.error("Add Virtual Commute Error:", error);
      alert(error);
    }
  };

  const updateVirtualCommuteAvailability = async (data) => {
    try {
      const response = await axios.post(
        UPDATE_VIRTUALCOMMUTE_AVAILABILITY,
        data,
        { headers: generateURLOptions() }
      );
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      getVirtualCommute();
    } catch (error) {
      console.error("Update Virtual Commute Availability Error:", error);
      alert(error);
    }
  };

  const updateVirtualCommute = async (data) => {
    console.log("Virtual Commute Data: ", data);
    try {
      const response = await axios.post(UPDATE_VIRTUAL_COMMUTE, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      setShowEditCommute(false);
      setTimeout(() => {
        getVirtualCommute();
      }, 250);
    } catch (error) {
      console.error("Update Virtual Commute Availability Error:", error);
      alert(error);
    }
  };

  const updateVirtualCommutePosition = async (data) => {
    console.log("VirtualCommute Data: ", data);
    try {
      const response = await axios.post(UPDATE_VIRTUALCOMMUTE_POSITION, data, {
        headers: generateURLOptions(),
      });
      if (response?.data?.success === false) {
        return alert(response?.data?.message ?? "");
      }
      getVirtualCommute();
    } catch (error) {
      console.error("Update Mindfulness Position Error: ", error);
      alert(error);
    }
  };

  const deleteVirtualCommuteItem = (id) => {
    Swal.fire({
      title: "Delete Virtual Commute Item",
      text: "Deleting a Virtual Commute Item is irreversible. Please confirm you want to delete this item.",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete Virtual Commute Item",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            DELETE_VIRTUAL_COMMUTE,
            { itemId: id },
            { headers: generateURLOptions() }
          );
          if (response?.data?.success === false) {
            return alert(response?.data?.message ?? "");
          }
          getVirtualCommute();
        } catch (error) {
          console.error("Delete Virtual Commute Error:", error);
          alert(error);
        }
      }
    });
  };

  // 👇 Snapdrag droppable grid
  const { droppable } = useDroppable({
    accepts: kind,
    onDrop: ({ data, event }) => {
      const sourceId = data.id;
      const newList = [...virtualCommutes];
      const moving = newList.find((m) => String(m._id) === String(sourceId));
      if (!moving) return;

      // --- grid index calc ---
      const gridRect = containerRef.current.getBoundingClientRect();
      const cell = containerRef.current.children[0]?.getBoundingClientRect();
      if (!cell) return;

      const cellWidth = cell.width + GAP;
      const cellHeight = cell.height + GAP;

      const col = Math.floor((event.clientX - gridRect.left) / cellWidth);
      const row = Math.floor((event.clientY - gridRect.top) / cellHeight);

      let targetIndex = row * GRID_COLUMNS + col;
      if (targetIndex < 0) targetIndex = 0;
      if (targetIndex >= newList.length) targetIndex = newList.length - 1;

      console.log("Dragged:", sourceId, "Dropped at index:", targetIndex);

      const oldIndex = newList.findIndex(
        (m) => String(m._id) === String(sourceId)
      );
      newList.splice(oldIndex, 1);
      newList.splice(targetIndex, 0, moving);

      setVirtualCommutes(newList);
      updateVirtualCommutePosition({
        orderedIds: newList.map((vc) => vc._id),
      });
    },
  });

  return (
    <>
      <AddVirtualCommute
        isOpen={showAddVirtualCommute}
        onClose={() => setShowAddVirtualCommmute(false)}
        onSubmit={addVirtualCommute}
      />
      <EditVirtualCommute
        isOpen={showEditCommute}
        onClose={() => setShowEditCommute(false)}
        item={selectedCommute}
        onSubmit={updateVirtualCommute}
      />

      <div className="w-full px-8">
        {/* Heading Area */}
        <div className="flex flex-row mt-8">
          <div className="flex flex-col">
            <h4 className="font-medium text-lg">Virtual Commute Sessions</h4>
            <p className="font-light text-base">
              {virtualCommutes?.length ?? 0} Items
            </p>
          </div>
          <div className="flex-1" />
          <div className="w-1/6">
            <FilledButton
              text="Add"
              icon={<TiPlus className="text-white mr-2" />}
              onPress={() => setShowAddVirtualCommmute(true)}
              borderRadius={96}
            />
          </div>
        </div>

        {/* Snapdrag Grid */}
        {droppable(
          <div ref={containerRef} className="grid grid-cols-5 gap-2">
            {virtualCommutes?.map((commute, index) => (
              <VirtualCommuteItemDraggable
                key={commute._id}
                item={commute}
                index={index}
                kind={kind}
                onEdit={editCommute}
                onDelete={deleteVirtualCommuteItem}
                onPlay={handleAudioPlay}
                onPause={handleAudioStop}
                onChangeStatus={updateVirtualCommuteAvailability}
              />
            ))}
          </div>
        )}
      </div>

      {/* 👇 Ghost Overlay */}
      <Overlay>
        {({ data }) => {
          if (!data?.item) return null;
          return (
            <div className="border p-2 bg-white rounded-md shadow-xl opacity-90">
              <VirtualCommuteItem
                item={data.item}
                onEdit={editCommute}
                onDelete={deleteVirtualCommuteItem}
                onPlay={handleAudioPlay}
                onPause={handleAudioStop}
                onChangeStatus={updateVirtualCommuteAvailability}
              />
            </div>
          );
        }}
      </Overlay>

      <Toast onClose={handleAudioStop} show={showAudio} delay={3000}>
        <Toast.Body>
          <Toast.Header className="border-0 p-0 pe-2">
            <audio
              src={playAudio?.audio ?? ""}
              controls
              autoPlay
              preload="auto"
            />
          </Toast.Header>
        </Toast.Body>
      </Toast>
    </>
  );
};

export default VirtualCommute;
